import type { ContentDatabase as NativeContentDatabase } from "@-/dev.improve.haniverse.database.content";
import type { StaticDatabase as NativeStaticDatabase } from "@-/dev.improve.haniverse.database.static";
import type { AnySQLiteColumn } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { contentSchema } from "@-/dev.improve.haniverse.database.schemas.content.schema";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { and, eq, inArray, like, not, or, type SQL, sql } from "@-/com.npmjs.drizzle-orm";
import {
  type MorphemeCorpusFilter,
  wordCorpusSqlCondition,
} from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import runes from "@-/com.npmjs.runes2";
import { hasLength, hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import { WORD_DEFINITION_FTS_TABLE } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { isPinyinSearchQuery, startsPinyinSyllable } from "#is-pinyin-search-query";

export const SEARCH_PAGE_SIZE = 40;

const LATIN_LIKE_QUERY_REGEX = /^[a-z0-9]+(?:[\s'-][a-z0-9]+)*$/i;

// Matches a single Han character (CJK Unified Ideographs + Extension A +
// compatibility ideographs). Used to detect a "sentence" query — several Han
// characters typed together — which is ordered positionally rather than ranked.
const HANIVERSE_CHARACTER_REGEX = /[\u{3400}-\u{4DBF}\u{4E00}-\u{9FFF}\u{F900}-\u{FAFF}]/u;

/**
 * A query of two or more distinct Han characters is treated as a *sentence*:
 * the user typed something they are reading and wants to step through its
 * characters in order. Results are ordered by position in the query and the
 * relevance ranking is disabled — see the ordering branch in `searchMorphemes`.
 *
 * Exported so the results screen can skip its own frequency re-sort and the
 * obscure-morpheme partition, both of which would scramble sentence order.
 */
export function isSentenceSearchQuery(query: string): boolean {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length === 0 || LATIN_LIKE_QUERY_REGEX.test(trimmedQuery)) {
    return false;
  }
  const hanCharacters = runes(trimmedQuery).filter(
    (c) => c.trim() && HANIVERSE_CHARACTER_REGEX.test(c),
  );
  return new Set(hanCharacters).size >= 2;
}

// Per-source weights. Each source contributes its weight once per glyph, then
// we aggregate across sources and add the per-glyph bonuses (glyph-position and
// best-pinyin-prefix). We deliberately do not score against long-form
// card-specific fields (meaning-card description, actor info notes) — the
// user is searching for morphemes, not prose.
//
// Gloss vs definition: the *gloss* is the short curated headline the tile
// actually renders (火 → "fire"), stored on the content DB's meaning /
// pronunciation cards. The *definition* is the long dictionary entry in the
// static DB (离 → "one of the Eight Trigrams 八卦, symbolizing fire"). A gloss
// match is what the user sees, so it must always outrank a definition match
// that merely mentions the query somewhere in a sentence — otherwise a search
// for "fire" surfaces 着 ("-ing") above 火 ("fire") with no visible reason.
const GLYPH_MATCH_SCORE = 350;
const PINYIN_MATCH_SCORE = 250;
const GLOSS_MATCH_SCORE = 600;
const DEFINITION_MATCH_SCORE = 100;

// Caps on the word pass. A bare CJK query like 一 is a substring of tens of
// thousands of dictionary entries, so the SQL is bounded before it reaches the
// scorer (`_ROW_LIMIT`) and the scored set is trimmed to what a results grid can
// usefully show (`_RESULT_LIMIT`). Without the row cap a single-character query
// pulls the whole `word` table over the D1 transport on web.
//
// The cap is paired with an `ORDER BY word.min_rank` on every word query, and
// the two only work together. `LIKE '%x%'` cannot use an index, so both word
// passes are full scans (`SCAN wd` over 281k `word_definition` rows, 257k over
// `word`). Capping a scan alone keeps whichever rows the table happened to
// yield first: measured on the shipped static.db, a bare `LIMIT 300` for 水 kept
// 下水道/墨水/口水 (best rank 5850) and dropped 水平/水果/香水 (rank 2565) — faster,
// but visibly worse. Ordering by the pre-aggregated, indexed `min_rank` makes
// the cap keep the COMMON words instead, and is also what lets SQLite stop
// early: 水 goes from ~48ms to ~14ms while returning better results.
//
// A latin definition scan (e.g. "fire") still costs ~85ms — its predicate has to
// be evaluated against every row before ordering can help. Only a real FTS index
// beats that, and it is a static-DB build change (see the package readme).
const WORD_MATCH_ROW_LIMIT = 400;
const WORD_RESULT_LIMIT = 200;

const EXACT_GLYPH_BONUS = 2000;
const CONTAINS_GLYPH_BONUS = 1200;
const EXACT_PINYIN_BONUS = 1000;
const PINYIN_PREFIX_BONUS = 700;
const PINYIN_CONTAINS_BONUS = 400;

// Taken off the pinyin bonus when the matching reading is one learners rarely
// meet (识 is shí; zhì survives only in 标识), so "zhi" lists every character
// whose everyday reading is zhi before 识. Kept below the gaps between the
// exact, prefix and contains bonuses so a rare reading of the typed syllable
// still precedes the longer syllables it prefixes ("zhu" lists 属's zhǔ before
// 转 zhuǎn), and those precede the syllables that only contain it.
const UNCOMMON_READING_PENALTY = 150;

// A non-main reading still counts as common while a curriculum places it below
// its advanced band (HSK 7-9, TBCL 5-7): 长 zhǎng is HSK 3, 行 háng is HSK 3.
const COMMON_READING_MAX_HSK_LEVEL = 6;
const COMMON_READING_MAX_TBCL_LEVEL = 4;

// Readings are stored in tone-number form ("zhi4"), so a toneless query
// ("zhi") is compared against the reading with its tone digit removed.
const PINYIN_TONE_DIGIT_REGEX = /[0-5]$/;

/**
 * How a dictionary reading matches a latin query. The first two are exact matches:
 *
 * - `syllable` when it is the typed syllable, with or without a tone digit;
 * - `final` when the query is a final no syllable starts with ("iu", "ong") and the reading ends
 *   with it ("iu" → jiu3, "iu3" → liu3);
 * - `prefix` when the user may still be typing a longer syllable ("zhu" → zhuan3);
 * - `contains` when the query is elsewhere in it ("an" → ban4).
 */
function gradePinyinReading(
  reading: string,
  normalizedQuery: string,
  isFinalQuery: boolean,
): "contains" | "final" | "prefix" | "syllable" | null {
  const lower = reading.toLocaleLowerCase();
  const toneless = lower.replace(PINYIN_TONE_DIGIT_REGEX, "");
  if (lower === normalizedQuery || toneless === normalizedQuery) {
    return "syllable";
  }
  if (isFinalQuery && (lower.endsWith(normalizedQuery) || toneless.endsWith(normalizedQuery))) {
    return "final";
  }
  if (lower.startsWith(normalizedQuery)) {
    return "prefix";
  }
  if (lower.includes(normalizedQuery)) {
    return "contains";
  }
  return null;
}

const PINYIN_BONUS_BY_GRADE = {
  contains: PINYIN_CONTAINS_BONUS,
  final: EXACT_PINYIN_BONUS,
  prefix: PINYIN_PREFIX_BONUS,
  syllable: EXACT_PINYIN_BONUS,
} as const;

// Specificity tiers, applied to whichever text source matched. A gloss/definition
// that *is* the query ("fire") is a far better hit than one that merely contains
// it among many other words ("pincers for use at a fire"), so we grade by how
// much of the matched text the query accounts for. Applied on top of the source
// weight above, and taken as a max per (source, glyph).
const EXACT_TEXT_BONUS = 900;
const LEADING_TEXT_BONUS = 500;
const SHORT_TEXT_BONUS = 250;

// A matched text of at most this many words still counts as "about" the query
// rather than merely mentioning it (e.g. "beacon fire", "to catch fire").
const SHORT_TEXT_WORD_COUNT = 3;

// Common English lead-ins stripped before exact-match comparison so "to
// extinguish" still reads as an exact hit for the query "extinguish".
const TEXT_LEAD_IN_REGEX = /^(?:to|a|an|the)\s+/;

// Splits a multi-sense entry ("to remove; to fire") into individual senses.
const TEXT_SENSE_SPLIT_REGEX = /[;,]/;

// Punctuation a whole-word match treats as a space. Space-padding alone missed
// every word followed by punctuation, so whether a common character matched
// depended on its dictionary's punctuation: 大 ("big; large") never matched
// "big" while 桓 ("big") did, and 门 ("gate; door") lost "door" to 扉. Apostrophes
// and hyphens are kept because they occur inside queries ("don't", "ice-cream").
const WORD_BOUNDARY_PUNCTUATION = [";", ",", ".", "!", "?", ":", '"', "/", "(", ")", "[", "]"];

/** `text` lowercased and space-padded with punctuation as spaces, for ` ${query} ` containment. */
function toWholeWordText(text: string): string {
  let out = text.toLocaleLowerCase();
  for (const mark of WORD_BOUNDARY_PUNCTUATION) {
    out = out.replaceAll(mark, " ");
  }
  return ` ${out} `;
}

/** SQL counterpart of {@link toWholeWordText}. */
function wholeWordTextSql(column: AnySQLiteColumn): SQL<string> {
  let text = sql`lower(${column})`;
  for (const mark of WORD_BOUNDARY_PUNCTUATION) {
    text = sql`replace(${text}, ${mark}, ' ')`;
  }
  return sql<string>`(' ' || ${text} || ' ')`;
}

/**
 * `column` contains the latin `normalizedQuery` as a whole word. The plain substring test runs
 * first so the punctuation rewrite is only evaluated on candidate rows; without it the rewrite
 * made the 281k-row `word_definition` scan ~5x slower.
 */
function likeWholeWord(column: AnySQLiteColumn, normalizedQuery: string): SQL | undefined {
  return and(
    like(column, `%${normalizedQuery}%`),
    like(wholeWordTextSql(column), `% ${normalizedQuery} %`),
  );
}

/**
 * `column` has a word that starts with the latin `normalizedQuery`: "mal" finds "malaria" but not
 * "normal". A superset of {@link likeWholeWord}; {@link gradeTextMatch} tells the two apart.
 */
function likeWordPrefix(column: AnySQLiteColumn, normalizedQuery: string): SQL | undefined {
  return and(
    like(column, `%${normalizedQuery}%`),
    like(wholeWordTextSql(column), `% ${normalizedQuery}%`),
  );
}

/**
 * A result is an *exact* match when it matches the whole query and a *partial* match when it only
 * starts with it, so Search can list the partial matches apart, below the exact ones.
 *
 * - Text (glosses, mnemonic aliases, definitions): exact when a latin query is a whole word of it
 *   ("fire" in "to catch fire"), partial when it only begins one ("mal" in "malaria"). A CJK query
 *   is exact only as the morpheme itself, so a definition that mentions it ("variant of 火") is
 *   partial.
 * - Readings: exact when the reading is the typed syllable, or ends with a typed final ("iu" →
 *   jiǔ); partial when it only starts with or contains it ("zhu" → zhuǎn, "an" → bàn).
 * - Words: exact when the written form or the whole reading is the query, partial when it only
 *   contains or starts with it (火车 for 火).
 */
export type SearchMatchKind = "exact" | "partial";

export interface SearchMorphemesOptions {
  /** Return exact matches only, skipping the partial-match passes. */
  exactOnly?: boolean;
  /**
   * Whether a latin query is also matched against readings, as a substring of them. Omitted, it is
   * detected from the query ({@link isPinyinSearchQuery}); `false` is the learner turning pinyin
   * off for an English word that spells a syllable ("you"), and `true` turning it on for text that
   * is not detected, such as a final ("iu" finds jiǔ, liú and niú).
   */
  pinyin?: boolean;
}

function gradeTextMatch(
  text: string,
  normalizedQuery: string,
  isLatinLikeQuery: boolean,
): SearchMatchKind {
  return isLatinLikeQuery && toWholeWordText(text).includes(` ${normalizedQuery} `)
    ? "exact"
    : "partial";
}

/**
 * The SQL test a gloss or definition column must pass (short latin queries have their own). Latin
 * queries match whole words, or also word starts unless `exactOnly`; CJK queries match any
 * substring, which is always partial (see {@link SearchMatchKind}).
 */
function textMatchCondition(
  column: AnySQLiteColumn,
  {
    exactOnly,
    isLatinLikeQuery,
    normalizedQuery,
    pattern,
  }: { exactOnly: boolean; isLatinLikeQuery: boolean; normalizedQuery: string; pattern: string },
): SQL | undefined {
  if (!isLatinLikeQuery) {
    return like(column, pattern);
  }
  return exactOnly
    ? likeWholeWord(column, normalizedQuery)
    : likeWordPrefix(column, normalizedQuery);
}

/**
 * Normalize a gloss/definition for specificity grading: lowercase, drop
 * parenthesised qualifiers ("(literary) fire" — metadata, not meaning), other
 * punctuation, and a leading "to "/"a "/"the ", and collapse whitespace.
 * Semicolon-separated senses are graded individually by the caller.
 *
 * The qualifier/whitespace patterns are built per call rather than hoisted to
 * module scope because `g`-flagged regexes carry mutable `lastIndex` state.
 */
function normalizeTextForGrading(text: string): string {
  return toWholeWordText(text.replaceAll(/\([^)]*\)/g, " "))
    .trim()
    .replace(TEXT_LEAD_IN_REGEX, "")
    .replaceAll(/\s+/g, " ")
    .trim();
}

/**
 * Grade how specifically `text` matches `normalizedQuery`, returning a bonus on
 * top of the source weight. Multi-sense entries ("to remove; to fire") are split
 * on `;`/`,` and graded per sense so one precise sense is not diluted by its
 * neighbours.
 */
function computeTextSpecificityBonus(text: string, normalizedQuery: string): number {
  let best = 0;
  for (const rawSense of text.split(TEXT_SENSE_SPLIT_REGEX)) {
    const sense = normalizeTextForGrading(rawSense);
    if (sense.length === 0) {
      continue;
    }
    let bonus = 0;
    if (sense === normalizedQuery) {
      bonus = EXACT_TEXT_BONUS;
    } else if (sense.startsWith(`${normalizedQuery} `)) {
      bonus = LEADING_TEXT_BONUS;
    } else if (!hasMinLength(sense.split(" "), SHORT_TEXT_WORD_COUNT + 1)) {
      bonus = SHORT_TEXT_BONUS;
    }
    if (bonus > best) {
      best = bonus;
    }
  }
  return best;
}

/**
 * Reduce a multi-sense entry to just the sense containing the query, so the
 * explanation the UI shows actually contains the word the user searched for.
 * 刷's only "fire" definition is "(fig.) to remove; to eliminate (from a
 * contest); to fire (from a job)" — the whole string truncates on a tile long
 * before "fire", so we surface "to fire (from a job)" instead.
 *
 * Falls back to the full text when no single sense contains the query (a CJK
 * substring query, or a match spanning a sense boundary).
 */
function extractMatchingSense(
  text: string,
  normalizedQuery: string,
  matchKind: SearchMatchKind = "exact",
): string {
  const senses = text.split(TEXT_SENSE_SPLIT_REGEX);
  if (!hasMinLength(senses, 2)) {
    return text.trim();
  }
  // Whole-word containment, or a word start for a partial match, as the SQL selected this row.
  const needle = matchKind === "exact" ? ` ${normalizedQuery} ` : ` ${normalizedQuery}`;
  let best: string | null = null;
  for (const rawSense of senses) {
    const sense = rawSense.trim();
    if (sense.length === 0) {
      continue;
    }
    if (!toWholeWordText(sense).includes(needle)) {
      continue;
    }
    // Prefer the most specific (shortest) sense that still contains the query.
    if (best === null || sense.length < best.length) {
      best = sense;
    }
  }
  return best ?? text.trim();
}

type GlyphScoreMap = Map<string, number>;

function addScore(map: GlyphScoreMap, glyph: string, score: number): void {
  const current = map.get(glyph);
  if (current === undefined || score > current) {
    map.set(glyph, score);
  }
}

/**
 * The specific gloss/definition text that caused a glyph to match, surfaced to
 * the UI so a result whose tile gloss does not contain the query can explain
 * itself (着 shows "-ing" but matched on the sense "to catch fire").
 */
export interface SearchMatchedText {
  /** Reading the matched text belongs to, when the source is reading-scoped. */
  pinyin: string | null;
  source: "definition" | "gloss";
  text: string;
}

type MatchedTextMap = Map<string, SearchMatchedText & { rank: number }>;

/**
 * Which morpheme stream a hit came from, so the Search toolbar's pills can scope
 * the rendered results without re-running the query.
 *
 * There is deliberately no `primitive` member. A primitive is a *character* that
 * is decomposition-only — a grapheme with no PRC tier — and the resolved slice
 * already carries that as `tier === null` (populated from `grapheme_prc_data.tier`
 * by `getMorphemeDetailsTileSlices`), the same definition Browse -> Primitives
 * uses. So the Characters tab's "Show Primitives" filter is a predicate on the
 * slice, not a separate stream to query.
 */
export type SearchResultCategory = "character" | "word";

/**
 * A search hit: the meaning-card id plus the text that caused it to match, so
 * the result tile can show why it is in the list when its own gloss does not
 * contain the query.
 *
 * `category` lets the results screen partition hits by the toolbar's tab; `front`
 * carries the surface form for word hits, which have no content-DB meaning card
 * and are resolved by their written form rather than by card id.
 */
export interface SearchMorphemesResult {
  category: SearchResultCategory;
  /** Surface form, for hits resolved by written form (words) rather than card id. */
  front: string | null;
  id: number;
  matchedText: SearchMatchedText | null;
  /** Every exact hit precedes every partial one, which Search lists below a divider. */
  matchKind: SearchMatchKind;
}

// Gloss matches explain a result better than definition matches, and a more
// specific hit explains it better than a vague one — so the text we surface is
// picked by the same ordering the score uses.
const MATCHED_TEXT_SOURCE_RANK = { definition: 0, gloss: 1 } as const;

/**
 * Keep the single best matched text per glyph: highest source rank first, then
 * the shortest text (a tight "fire" beats a rambling sense that also matched).
 */
function recordMatchedText(map: MatchedTextMap, glyph: string, matched: SearchMatchedText): void {
  const text = matched.text.trim();
  if (text.length === 0) {
    return;
  }
  const rank = MATCHED_TEXT_SOURCE_RANK[matched.source];
  const current = map.get(glyph);
  if (
    current === undefined ||
    rank > current.rank ||
    (rank === current.rank && text.length < current.text.length)
  ) {
    map.set(glyph, { ...matched, rank, text });
  }
}

/**
 * Per-source glyph match accumulator. We accumulate scores per (source, glyph)
 * separately because the original SQL produced one row per (source, card_id)
 * and summed them. To preserve that, we keep independent maps and sum.
 */
interface PerSourceGlyphScores {
  glyph: GlyphScoreMap;
  pinyin: GlyphScoreMap;
  /** Each glyph's readings that contain the query, which `gradePinyinMatchByGlyph` grades. */
  pinyinReadings: Map<string, string[]>;
}

/**
 * Gloss and definition matches of one {@link SearchMatchKind}. Exact and partial matches are
 * scored apart, so a partial match never lifts an exact one: "fire" ranks 火 exactly as it did
 * before partial matches existed, however many "firearm" senses also match.
 */
interface TextMatches {
  definition: GlyphScoreMap;
  gloss: GlyphScoreMap;
  matchedText: MatchedTextMap;
}

type TextMatchesByKind = Record<SearchMatchKind, TextMatches>;

function createTextMatchesByKind(): TextMatchesByKind {
  return {
    exact: { definition: new Map(), gloss: new Map(), matchedText: new Map() },
    partial: { definition: new Map(), gloss: new Map(), matchedText: new Map() },
  };
}

const TEXT_SOURCE_SCORE = { definition: DEFINITION_MATCH_SCORE, gloss: GLOSS_MATCH_SCORE } as const;

/** Score a matched gloss/definition `text` for `glyph`, under the kind of match it is. */
function recordTextMatch(
  textMatches: TextMatchesByKind,
  glyph: string,
  {
    isLatinLikeQuery,
    normalizedQuery,
    pinyin,
    source,
    text,
  }: {
    isLatinLikeQuery: boolean;
    normalizedQuery: string;
    pinyin: string | null;
    source: SearchMatchedText["source"];
    text: string;
  },
): void {
  const matchKind = gradeTextMatch(text, normalizedQuery, isLatinLikeQuery);
  const matches = textMatches[matchKind];
  addScore(
    matches[source],
    glyph,
    TEXT_SOURCE_SCORE[source] + computeTextSpecificityBonus(text, normalizedQuery),
  );
  recordMatchedText(matches.matchedText, glyph, {
    pinyin,
    source,
    text: extractMatchingSense(text, normalizedQuery, matchKind),
  });
}

async function collectGlyphScoresFromStatic({
  exactOnly,
  isLatinLikeQuery,
  isPinyinSearch,
  isShortLatinLikeQuery,
  normalizedQuery,
  pattern,
  queryCharacters,
  staticDb,
  textMatches,
  trimmedQuery,
}: {
  exactOnly: boolean;
  isLatinLikeQuery: boolean;
  isPinyinSearch: boolean;
  isShortLatinLikeQuery: boolean;
  normalizedQuery: string;
  pattern: string;
  queryCharacters: string[];
  staticDb: StaticDatabase;
  textMatches: TextMatchesByKind;
  trimmedQuery: string;
}): Promise<PerSourceGlyphScores> {
  const out: PerSourceGlyphScores = {
    glyph: new Map(),
    pinyin: new Map(),
    pinyinReadings: new Map(),
  };

  // --- Glyph matches: `glyph IN (queryChars) OR glyph LIKE %query%`.
  if (isNonEmpty(queryCharacters) || trimmedQuery.length > 0) {
    const conditions = [];
    if (isNonEmpty(queryCharacters)) {
      conditions.push(inArray(staticSchema.grapheme.glyph, queryCharacters));
    }
    conditions.push(like(staticSchema.grapheme.glyph, pattern));
    const glyphRows = await staticDb(import.meta.location)
      .select({ glyph: staticSchema.grapheme.glyph })
      .from(staticSchema.grapheme)
      .where(or(...conditions));
    for (const row of glyphRows) {
      addScore(out.glyph, row.glyph, GLYPH_MATCH_SCORE);
    }
  }

  // --- Pinyin matches: per-grapheme join to find every glyph with a reading that contains the
  // query. Whether the match is exact or partial is graded per glyph by `gradePinyinMatchByGlyph`.
  if (isPinyinSearch) {
    const pinyinRows = await staticDb(import.meta.location)
      .select({
        glyph: staticSchema.grapheme.glyph,
        pinyin: staticSchema.graphemeDefinition.pinyin,
      })
      .from(staticSchema.graphemeDefinition)
      .innerJoin(
        staticSchema.grapheme,
        eq(staticSchema.grapheme.id, staticSchema.graphemeDefinition.graphemeId),
      )
      .where(like(staticSchema.graphemeDefinition.pinyin, `%${normalizedQuery}%`));
    for (const row of pinyinRows) {
      addScore(out.pinyin, row.glyph, PINYIN_MATCH_SCORE);
      const readings = out.pinyinReadings.get(row.glyph) ?? [];
      readings.push(row.pinyin);
      out.pinyinReadings.set(row.glyph, readings);
    }
  }

  // --- Definition matches. A CJK query only ever mentions itself in a definition, which is a
  // partial match, so an exact-only CJK search has nothing to read here.
  if (!isShortLatinLikeQuery && (isLatinLikeQuery || !exactOnly)) {
    const definitionCondition = textMatchCondition(staticSchema.graphemeDefinition.definition, {
      exactOnly,
      isLatinLikeQuery,
      normalizedQuery,
      pattern,
    });
    const definitionRows = await staticDb(import.meta.location)
      .select({
        definition: staticSchema.graphemeDefinition.definition,
        glyph: staticSchema.grapheme.glyph,
        pinyin: staticSchema.graphemeDefinition.pinyin,
      })
      .from(staticSchema.graphemeDefinition)
      .innerJoin(
        staticSchema.grapheme,
        eq(staticSchema.grapheme.id, staticSchema.graphemeDefinition.graphemeId),
      )
      .where(definitionCondition);
    for (const row of definitionRows) {
      recordTextMatch(textMatches, row.glyph, {
        isLatinLikeQuery,
        normalizedQuery,
        pinyin: row.pinyin,
        source: "definition",
        text: row.definition ?? "",
      });
    }
  }

  return out;
}

/**
 * Card-gloss matches, read from the content DB. This is the text the result
 * tile actually renders, so it is both the highest-weighted source and the
 * preferred explanation when we surface *why* a card matched.
 *
 * Both meaning-card glosses (火 → "fire") and per-reading pronunciation-card
 * glosses are searched; the latter is what lets 着's "to catch fire" reading
 * rank on its own merits rather than on its "-ing" headline.
 * Component mnemonic aliases (十's "cross") share the gloss score and
 * explanation, so any authored mnemonic can find its card without duplicate
 * aliases boosting it.
 */
async function collectGlossScoresFromContent({
  contentDb,
  exactOnly,
  isLatinLikeQuery,
  isShortLatinLikeQuery,
  normalizedQuery,
  pattern,
  textMatches,
}: {
  contentDb: ContentDatabase;
  exactOnly: boolean;
  isLatinLikeQuery: boolean;
  isShortLatinLikeQuery: boolean;
  normalizedQuery: string;
  pattern: string;
  textMatches: TextMatchesByKind;
}): Promise<void> {
  // Glosses and aliases are English, so a CJK query can only match one partially.
  if (!isLatinLikeQuery && exactOnly) {
    return;
  }

  // Whole words and word starts for latin queries (see `textMatchCondition`), so
  // "fire" is an exact match for "fire" and a partial one for "firearm".
  const buildGlossCondition = (column: AnySQLiteColumn) => {
    // Short mnemonics such as "ax" are useful exact lookups, but should not
    // expand into every gloss or mnemonic containing that short word.
    if (isShortLatinLikeQuery) {
      return eq(sql<string>`lower(trim(${column}))`, normalizedQuery);
    }
    return textMatchCondition(column, { exactOnly, isLatinLikeQuery, normalizedQuery, pattern });
  };

  const [meaningRows, pronunciationRows, mnemonicRows] = await Promise.all([
    contentDb(import.meta.location)
      .select({
        gloss: contentSchema.appGraphemeMeaningCard.gloss,
        glyph: contentSchema.appGraphemeMeaningCard.grapheme,
      })
      .from(contentSchema.appGraphemeMeaningCard)
      .where(buildGlossCondition(contentSchema.appGraphemeMeaningCard.gloss)),
    contentDb(import.meta.location)
      .select({
        gloss: contentSchema.appGraphemePronunciationCard.gloss,
        glyph: contentSchema.appGraphemePronunciationCard.grapheme,
        pinyin: contentSchema.appGraphemePronunciationCard.pinyin,
      })
      .from(contentSchema.appGraphemePronunciationCard)
      .where(buildGlossCondition(contentSchema.appGraphemePronunciationCard.gloss)),
    contentDb(import.meta.location)
      .select({
        gloss: contentSchema.appGraphemeMeaningCardAlias.alias,
        glyph: contentSchema.appGraphemeMeaningCard.grapheme,
      })
      .from(contentSchema.appGraphemeMeaningCardAlias)
      .innerJoin(
        contentSchema.appGraphemeMeaningCard,
        eq(
          contentSchema.appGraphemeMeaningCard.id,
          contentSchema.appGraphemeMeaningCardAlias.appGraphemeMeaningCardId,
        ),
      )
      .where(buildGlossCondition(contentSchema.appGraphemeMeaningCardAlias.alias)),
  ]);

  for (const row of [...meaningRows, ...mnemonicRows]) {
    recordTextMatch(textMatches, row.glyph, {
      isLatinLikeQuery,
      normalizedQuery,
      pinyin: null,
      source: "gloss",
      text: row.gloss ?? "",
    });
  }
  for (const row of pronunciationRows) {
    recordTextMatch(textMatches, row.glyph, {
      isLatinLikeQuery,
      normalizedQuery,
      pinyin: row.pinyin,
      source: "gloss",
      text: row.gloss,
    });
  }
}

/**
 * The readings learners actually meet, per glyph: the main pronunciation card
 * plus any other card a curriculum places below its advanced band. Glyphs the
 * app has no pronunciation cards for are absent, which leaves every one of
 * their readings ungraded rather than uncommon.
 */
async function fetchCommonReadingsByGlyph(
  contentDb: ContentDatabase,
  glyphs: string[],
): Promise<Map<string, Set<string>>> {
  const out = new Map<string, Set<string>>();
  if (hasLength(glyphs, 0)) {
    return out;
  }
  const rows = await contentDb(import.meta.location)
    .select({
      glyph: contentSchema.appGraphemePronunciationCard.grapheme,
      hsk2026Level: contentSchema.appGraphemePronunciationCard.hsk2026Level,
      isMain: contentSchema.appGraphemePronunciationCard.isMain,
      pinyin: contentSchema.appGraphemePronunciationCard.pinyin,
      tbclLevel: contentSchema.appGraphemePronunciationCard.tbclLevel,
    })
    .from(contentSchema.appGraphemePronunciationCard)
    .where(inArray(contentSchema.appGraphemePronunciationCard.grapheme, glyphs));
  for (const row of rows) {
    const readings = out.get(row.glyph) ?? new Set<string>();
    out.set(row.glyph, readings);
    if (
      row.isMain ||
      (row.hsk2026Level !== null && row.hsk2026Level <= COMMON_READING_MAX_HSK_LEVEL) ||
      (row.tbclLevel !== null && row.tbclLevel <= COMMON_READING_MAX_TBCL_LEVEL)
    ) {
      readings.add(row.pinyin.toLocaleLowerCase());
    }
  }
  return out;
}

interface PinyinMatch {
  /** Bonus of the glyph's best-matching reading. */
  bonus: number;
  /** Whether one of the glyph's readings matches exactly (see {@link gradePinyinReading}). */
  isExact: boolean;
  /** Whether one of the glyph's readings IS the typed syllable. */
  isOwnReading: boolean;
}

/**
 * Best-pinyin match per glyph: a 1000 bonus if any reading matches exactly,
 * else 700 if any reading starts with the query, else 400 if any contains it,
 * less {@link UNCOMMON_READING_PENALTY} when the best such reading is not one
 * of the glyph's common readings. Grades the readings the pinyin pass already
 * read, for the glyphs that survived the per-card pipeline.
 */
async function gradePinyinMatchByGlyph(
  contentDb: ContentDatabase,
  pinyinReadings: ReadonlyMap<string, string[]>,
  glyphs: string[],
  normalizedQuery: string,
): Promise<Map<string, PinyinMatch>> {
  const out = new Map<string, PinyinMatch>();
  const readingGlyphs = glyphs.filter((glyph) => pinyinReadings.has(glyph));
  if (hasLength(readingGlyphs, 0)) {
    return out;
  }
  // No syllable starts with a final such as "iu", so a reading matches it exactly by ending in it.
  const isFinalQuery = !startsPinyinSyllable(normalizedQuery.replace(PINYIN_TONE_DIGIT_REGEX, ""));
  const commonReadingsByGlyph = await fetchCommonReadingsByGlyph(contentDb, readingGlyphs);
  for (const glyph of readingGlyphs) {
    const commonReadings = commonReadingsByGlyph.get(glyph);
    for (const reading of pinyinReadings.get(glyph) ?? []) {
      const grade = gradePinyinReading(reading, normalizedQuery, isFinalQuery);
      if (grade === null) {
        continue;
      }
      const isUncommonReading =
        commonReadings !== undefined && !commonReadings.has(reading.toLocaleLowerCase());
      const bonus =
        PINYIN_BONUS_BY_GRADE[grade] - (isUncommonReading ? UNCOMMON_READING_PENALTY : 0);
      const current = out.get(glyph);
      out.set(glyph, {
        bonus: Math.max(bonus, current?.bonus ?? 0),
        isExact: grade === "syllable" || grade === "final" || current?.isExact === true,
        isOwnReading: grade === "syllable" || current?.isOwnReading === true,
      });
    }
  }
  return out;
}

/**
 * Best (smallest) SUBTLEX-CH character rank per glyph, used to order hits of
 * equal relevance: the four exact "this" glosses (这/斯/此/兹) tie on score, and
 * the common 这 belongs first rather than wherever collation puts it. Glyphs
 * without a SUBTLEX row are absent and sort after every ranked glyph.
 */
async function fetchSubtlexRankByGlyph(
  staticDb: StaticDatabase,
  glyphs: string[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (hasLength(glyphs, 0)) {
    return out;
  }
  const rows = await staticDb(import.meta.location)
    .select({
      glyph: staticSchema.grapheme.glyph,
      rank: staticSchema.graphemeSubtlex.rank,
    })
    .from(staticSchema.graphemeSubtlex)
    .innerJoin(
      staticSchema.grapheme,
      eq(staticSchema.grapheme.id, staticSchema.graphemeSubtlex.graphemeId),
    )
    .where(inArray(staticSchema.grapheme.glyph, glyphs));
  for (const row of rows) {
    const current = out.get(row.glyph);
    if (current === undefined || row.rank < current) {
      out.set(row.glyph, row.rank);
    }
  }
  return out;
}

/** Ascending SUBTLEX rank, unranked last. */
function compareSubtlexRank(a: number | null | undefined, b: number | null | undefined): number {
  return (a ?? Number.POSITIVE_INFINITY) - (b ?? Number.POSITIVE_INFINITY);
}

/**
 * Per-card glyph bonus: 2000 for exact match, 1200 if the card's glyph is a
 * substring of the trimmed query. Pure JS — derives from card.grapheme alone.
 */
function computeGlyphBonus(glyph: string, trimmedQuery: string): number {
  if (glyph === trimmedQuery) {
    return EXACT_GLYPH_BONUS;
  }
  if (trimmedQuery.includes(glyph)) {
    return CONTAINS_GLYPH_BONUS;
  }
  return 0;
}

/**
 * The lookup that resolves a hit to its renderable slice.
 *
 * Use the already-ranked front for both characters and words, avoiding redundant
 * ID-to-front reads before hydration. Legacy cached hits without a front still
 * resolve by ID so missing rows retain the pagination cursor behavior.
 */
export function searchResultToMorphemeLookup(
  result: SearchMorphemesResult,
): { morpheme: string } | { id: number } {
  return result.front !== null ? { morpheme: result.front } : { id: result.id };
}

/**
 * Words whose written form or English definition matches the query, read from the
 * static dictionary (`word` + `word_definition`).
 *
 * Words are a separate pass rather than a branch of the grapheme pipeline because
 * they are keyed by written FORM, not by a content-DB card: there is no
 * `app_word_meaning_card`, so a hit is resolved downstream by
 * `getMorphemeDetailsTileSlices`'s word fallback (`{ morpheme: word }`), which
 * builds the `morphemeType: "word"` slice from the same dictionary rows.
 *
 * Scoring mirrors the grapheme side so the two streams interleave sensibly in the
 * All tab: an exact form match outranks a containment, and a definition that *is*
 * the query outranks one that merely mentions it.
 */
/**
 * Whether this database carries the `word_definition_fts` full-text index.
 *
 * It exists in the local `static.db` / `static-lite.db` files but NOT in D1: the
 * deploy's delta sync reads the remote schema with `wrangler d1 export`, and D1
 * refuses to export a database containing a virtual table, so the index is built
 * only into the shipped files (see `word-definition-fts.ts`). Offline readers get
 * the fast path; the online proxy transport falls back to the `LIKE` scan.
 *
 * Cached per database handle: the probe is one `sqlite_master` read, but the word
 * search runs on every query and this must not add a round trip to the worker
 * each time. `WeakMap` so a closed/replaced handle does not pin the entry.
 */
const hasWordDefinitionFtsByDb = new WeakMap<StaticDatabase, Promise<boolean>>();

async function hasWordDefinitionFts(staticDb: StaticDatabase): Promise<boolean> {
  const cached = hasWordDefinitionFtsByDb.get(staticDb);
  if (cached !== undefined) {
    return cached;
  }
  const db = staticDb(import.meta.location);
  const probe = (async () => {
    try {
      const rows = await db.all<{ name: string }>(
        sql`select name from sqlite_master where type = 'table' and name = ${WORD_DEFINITION_FTS_TABLE} limit 1`,
      );
      return isNonEmpty(rows);
    } catch {
      // A transport that rejects the probe is treated as "no index" rather than
      // failing the search: the `LIKE` fallback returns the same rows, slower.
      return false;
    }
  })();
  hasWordDefinitionFtsByDb.set(staticDb, probe);
  return probe;
}

/**
 * Escape a user query for an FTS5 `MATCH`. FTS5 has its own query grammar in
 * which bare `-`, `*`, `"`, `:` and `^` are operators, so an unescaped query is
 * both a syntax-error risk and a way to smuggle operators in. Wrapping the whole
 * thing in double quotes (doubling any embedded ones) makes FTS5 read it as a
 * literal phrase, which is exactly the semantics the `LIKE` scan had.
 */
function toFtsPhrase(query: string): string {
  return `"${query.replaceAll('"', '""')}"`;
}

/** Score and explanation accumulators for word hits of one {@link SearchMatchKind}. */
interface WordMatches {
  definitionScore: GlyphScoreMap;
  lookupScore: GlyphScoreMap;
  matchedText: MatchedTextMap;
}

function createWordMatches(): WordMatches {
  return { definitionScore: new Map(), lookupScore: new Map(), matchedText: new Map() };
}

async function searchWords(
  staticDb: StaticDatabase,
  {
    corpusCondition,
    exactOnly,
    isLatinLikeQuery,
    isPinyinSearch,
    isShortLatinLikeQuery,
    normalizedQuery,
    pattern,
    trimmedQuery,
  }: {
    /** Extra `word` predicate from the user's corpus filter; ANDed into every word query. */
    corpusCondition: SQL | undefined;
    exactOnly: boolean;
    isLatinLikeQuery: boolean;
    isPinyinSearch: boolean;
    isShortLatinLikeQuery: boolean;
    normalizedQuery: string;
    pattern: string;
    trimmedQuery: string;
  },
): Promise<SearchMorphemesResult[]> {
  const db = staticDb(import.meta.location);
  const byRank = sql`coalesce(${staticSchema.word.minRank}, 999999)`;

  // Form matches. A CJK query looks for words CONTAINING it (searching 火 should
  // surface 火车, 大火, as partial matches); a latin query is never a word form, so
  // it is skipped.
  const formRows = isLatinLikeQuery
    ? []
    : await db
        .select({ minRank: staticSchema.word.minRank, word: staticSchema.word.word })
        .from(staticSchema.word)
        .where(
          and(
            exactOnly
              ? eq(staticSchema.word.word, trimmedQuery)
              : like(staticSchema.word.word, pattern),
            corpusCondition,
          ),
        )
        .orderBy(byRank)
        .limit(WORD_MATCH_ROW_LIMIT);

  // Definition matches. Whole words are exact and word starts partial for latin
  // queries ("fire" vs "firearm"); a CJK substring is always partial. Skipped for
  // 1-2 character latin queries, matching the grapheme side's
  // `isShortLatinLikeQuery` cutoff.
  //
  // Exact and partial matches are separate queries, each with its own row cap:
  // sharing one would let common partial matches crowd rarer exact ones out of it.
  const definitionRows: {
    definition: string;
    matchKind: SearchMatchKind;
    minRank: number | null;
    word: string;
  }[] = [];
  if (!isShortLatinLikeQuery && (isLatinLikeQuery || !exactOnly)) {
    // The full-text path, when this database has the index. `LIKE '%x%'` cannot
    // use an index, so the fallback below scans all ~281k `word_definition`
    // rows (~85-155ms measured); the FTS lookup is ~0.3ms.
    //
    // Both paths treat punctuation as a word boundary: FTS through its
    // tokenizer, the fallback through `likeWholeWord`, so 交火 ("to
    // exchange fire; to engage in a firefight") matches "fire" either way.
    //
    // CJK queries stay on `LIKE`: the `unicode61` tokenizer does not segment
    // Chinese, so a substring of a CJK definition is not a token and would not
    // match. Those queries are already served by the indexed form match above.
    const useFts = isLatinLikeQuery && (await hasWordDefinitionFts(staticDb));
    const definition = staticSchema.wordDefinition.definition;
    let exactCondition: SQL | undefined;
    let partialCondition: SQL | undefined;
    if (useFts) {
      const ftsTable = sql.identifier(WORD_DEFINITION_FTS_TABLE);
      const ftsMatch = (expression: string) =>
        sql`${staticSchema.wordDefinition.id} in (select rowid from ${ftsTable} where ${ftsTable} match ${expression})`;
      const phrase = toFtsPhrase(normalizedQuery);
      exactCondition = ftsMatch(phrase);
      // A trailing `*` makes the phrase a prefix query; `NOT` leaves the rows with
      // the whole word to the exact pass.
      partialCondition = ftsMatch(`${phrase}* NOT ${phrase}`);
    } else if (isLatinLikeQuery) {
      const wholeWord = likeWholeWord(definition, normalizedQuery);
      exactCondition = wholeWord;
      partialCondition = and(
        likeWordPrefix(definition, normalizedQuery),
        wholeWord === undefined ? undefined : not(wholeWord),
      );
    } else {
      partialCondition = like(definition, pattern);
    }
    const selectDefinitionRows = async (condition: SQL | undefined, matchKind: SearchMatchKind) => {
      if (condition === undefined || (exactOnly && matchKind === "partial")) {
        return [];
      }
      const rows = await db
        .select({
          definition: staticSchema.wordDefinition.definition,
          minRank: staticSchema.word.minRank,
          word: staticSchema.word.word,
        })
        .from(staticSchema.wordDefinition)
        .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordDefinition.wordId))
        .where(and(condition, corpusCondition))
        .orderBy(byRank)
        .limit(WORD_MATCH_ROW_LIMIT);
      return rows.map((row) => ({ ...row, matchKind }));
    };
    const [exactRows, partialRows] = await Promise.all([
      selectDefinitionRows(exactCondition, "exact"),
      selectDefinitionRows(partialCondition, "partial"),
    ]);
    definitionRows.push(...exactRows, ...partialRows);
  }

  // Pinyin matches. `word_definition.pinyin` is space-separated tone-number
  // syllables ("huo3 che1"), so a latin query is matched as a prefix on the whole
  // reading and is exact only as the whole reading. Unlike a character's
  // one-syllable reading, a word's is not searched for substrings: a word is
  // found by its reading as typed, and a substring scan cannot use the pinyin
  // index (~25-50ms over every `word_definition` row per keystroke). Skipped for
  // CJK queries, which are already covered by the form match above, and when
  // pinyin is off.
  const pinyinRows = isPinyinSearch
    ? await db
        .select({
          minRank: staticSchema.word.minRank,
          pinyin: staticSchema.wordDefinition.pinyin,
          word: staticSchema.word.word,
        })
        .from(staticSchema.wordDefinition)
        .innerJoin(staticSchema.word, eq(staticSchema.word.id, staticSchema.wordDefinition.wordId))
        .where(
          and(
            like(
              staticSchema.wordDefinition.pinyin,
              exactOnly ? normalizedQuery : `${normalizedQuery}%`,
            ),
            corpusCondition,
          ),
        )
        .orderBy(byRank)
        .limit(WORD_MATCH_ROW_LIMIT)
    : [];

  // Aggregate per word form. A form can match on several definitions; keep the
  // best score and the most specific explanation, exactly as the glyph side does.
  // Sources (form/pinyin vs definition) add, but repeated definition rows do not:
  // summing them let a word with many senses that merely mention "this" outrank
  // one whose definition IS "this". A word with any exact match is exact, and is
  // scored on its exact matches alone.
  const matchesByKind: Record<SearchMatchKind, WordMatches> = {
    exact: createWordMatches(),
    partial: createWordMatches(),
  };
  const subtlexRankByWord = new Map<string, number | null>();

  for (const row of formRows) {
    const isExact = row.word === trimmedQuery;
    addScore(
      matchesByKind[isExact ? "exact" : "partial"].lookupScore,
      row.word,
      GLYPH_MATCH_SCORE + (isExact ? EXACT_GLYPH_BONUS : CONTAINS_GLYPH_BONUS),
    );
    subtlexRankByWord.set(row.word, row.minRank);
  }

  for (const row of pinyinRows) {
    const isExact = row.pinyin.toLocaleLowerCase() === normalizedQuery;
    addScore(
      matchesByKind[isExact ? "exact" : "partial"].lookupScore,
      row.word,
      PINYIN_MATCH_SCORE + (isExact ? EXACT_PINYIN_BONUS : PINYIN_PREFIX_BONUS),
    );
    subtlexRankByWord.set(row.word, row.minRank);
  }

  for (const row of definitionRows) {
    const matches = matchesByKind[row.matchKind];
    addScore(
      matches.definitionScore,
      row.word,
      DEFINITION_MATCH_SCORE + computeTextSpecificityBonus(row.definition, normalizedQuery),
    );
    subtlexRankByWord.set(row.word, row.minRank);
    recordMatchedText(matches.matchedText, row.word, {
      pinyin: null,
      source: "definition",
      text: extractMatchingSense(row.definition, normalizedQuery, row.matchKind),
    });
  }

  const results = Array.from(subtlexRankByWord, ([word, subtlexRank]) => {
    const matchKind: SearchMatchKind =
      matchesByKind.exact.lookupScore.has(word) || matchesByKind.exact.definitionScore.has(word)
        ? "exact"
        : "partial";
    const matches = matchesByKind[matchKind];
    const matched = matches.matchedText.get(word);
    return {
      matchedText:
        matched === undefined
          ? null
          : { pinyin: matched.pinyin, source: matched.source, text: matched.text },
      matchKind,
      score: (matches.lookupScore.get(word) ?? 0) + (matches.definitionScore.get(word) ?? 0),
      subtlexRank,
      word,
    };
  });

  results.sort((a, b) => {
    if (a.score !== b.score) {
      return b.score - a.score;
    }
    // Equal relevance: the more common word first (这样 #68 before 今世 #51819).
    const rankDelta = compareSubtlexRank(a.subtlexRank, b.subtlexRank);
    if (rankDelta !== 0) {
      return rankDelta;
    }
    // Shorter words first: 火车 is a better answer for 火 than 火车站台.
    if (a.word.length !== b.word.length) {
      return a.word.length - b.word.length;
    }
    return a.word.localeCompare(b.word);
  });

  const toResult = (entry: (typeof results)[number]): SearchMorphemesResult => ({
    category: "word",
    front: entry.word,
    // Word hits carry no content-DB card id; downstream resolution is by `front`.
    id: -1,
    matchedText: entry.matchedText,
    matchKind: entry.matchKind,
  });
  return [
    ...results
      .filter((entry) => entry.matchKind === "exact")
      .slice(0, WORD_RESULT_LIMIT)
      .map((entry) => toResult(entry)),
    ...results
      .filter((entry) => entry.matchKind === "partial")
      .slice(0, WORD_RESULT_LIMIT)
      .map((entry) => toResult(entry)),
  ];
}

/**
 * Rank every character and word matching `query`: every exact match first, then every partial one
 * (see {@link SearchMatchKind}), each characters before words.
 *
 * `corpusFilter` (the user's browsing corpus, see `corpus_settings`) hides out-of-corpus
 * characters and words from the results — search is a non-curated surface. Direct lookups are
 * exempt: a glyph or word that IS the query (or, in sentence mode, one of the typed sentence's
 * characters) is always returned, since the user asked for exactly that morpheme. Omitted (the
 * anonymous/SEO worker callers), nothing is filtered.
 */
export async function searchMorphemes(
  contentDb: ContentDatabase,
  staticDb: StaticDatabase,
  query: string,
  corpusFilter?: MorphemeCorpusFilter,
  { exactOnly = false, pinyin }: SearchMorphemesOptions = {},
): Promise<SearchMorphemesResult[]> {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length === 0) {
    return [];
  }

  const normalizedQuery = trimmedQuery.toLocaleLowerCase();
  const isLatinLikeQuery = LATIN_LIKE_QUERY_REGEX.test(trimmedQuery);
  const isShortLatinLikeQuery = isLatinLikeQuery && normalizedQuery.length <= 2;
  // Readings are latin letters and a tone digit, so only a latin query can match one.
  const isPinyinSearch = isLatinLikeQuery && (pinyin ?? isPinyinSearchQuery(trimmedQuery));
  const pattern = `%${trimmedQuery}%`;
  const queryRunes = runes(trimmedQuery).filter((c) => c.trim());
  const queryCharacters = [...new Set(queryRunes)];

  // A query of several Han characters is a *sentence*, not a search term: the
  // user has typed something they are reading and wants to walk its characters
  // in order. Ranking them by score is meaningless there — every character
  // matches its own glyph identically, so the score ties and the tiebreaker
  // falls back to Unicode collation, scrambling the sentence (我在浴缸里泡澡 came
  // back as 在我泡浴澡缸里). In sentence mode we order by first position in the
  // query instead and skip ranking entirely.
  const sentenceHanCharacters = queryRunes.filter((c) => HANIVERSE_CHARACTER_REGEX.test(c));
  const isSentenceQuery = isSentenceSearchQuery(trimmedQuery);
  const sentencePositionByGlyph = new Map<string, number>();
  if (isSentenceQuery) {
    for (const [index, character] of sentenceHanCharacters.entries()) {
      if (!sentencePositionByGlyph.has(character)) {
        sentencePositionByGlyph.set(character, index);
      }
    }
  }

  // Gloss/definition scores per kind of match, with the specific text behind
  // each glyph's match, so the UI can explain results whose tile gloss does not
  // contain the query.
  const textMatches = createTextMatchesByKind();

  // 1) Per-glyph scores. Static side (glyph / pinyin / definition) and content
  // side (card gloss) are independent queries, so run them concurrently.
  //
  // The word pass is independent of both (it reads the static dictionary keyed by
  // written form) so it joins the same `Promise.all` rather than adding a round
  // trip. Words are searched on every query, not only when the Words tab is
  // active: the tab filters the rendered set, so switching tabs must not re-run
  // the search.
  // Words are filtered in SQL so each pass's LIMIT keeps in-corpus words; an exact word match
  // is a direct lookup and survives the filter.
  const corpusWordCondition =
    corpusFilter === undefined || corpusFilter.includesAllWords
      ? undefined
      : wordCorpusSqlCondition(corpusFilter);
  const wordCorpusCondition =
    corpusWordCondition === undefined
      ? undefined
      : or(eq(staticSchema.word.word, trimmedQuery), corpusWordCondition);

  // A sentence is walked through its own characters, each an exact match, so the partial passes
  // could only add noise to it.
  const skipsPartialMatches = exactOnly || isSentenceQuery;
  const [glyphScores, , wordResults] = await Promise.all([
    collectGlyphScoresFromStatic({
      exactOnly: skipsPartialMatches,
      isLatinLikeQuery,
      isPinyinSearch,
      isShortLatinLikeQuery,
      normalizedQuery,
      pattern,
      queryCharacters,
      staticDb,
      textMatches,
      trimmedQuery,
    }),
    collectGlossScoresFromContent({
      contentDb,
      exactOnly: skipsPartialMatches,
      isLatinLikeQuery,
      isShortLatinLikeQuery,
      normalizedQuery,
      pattern,
      textMatches,
    }),
    searchWords(staticDb, {
      corpusCondition: wordCorpusCondition,
      exactOnly: skipsPartialMatches,
      isLatinLikeQuery,
      isPinyinSearch,
      isShortLatinLikeQuery,
      normalizedQuery,
      pattern,
      trimmedQuery,
    }),
  ]);

  // 2) Resolve each matching glyph to its meaning card_id(s).
  const matchedGlyphs = new Set<string>([
    ...glyphScores.glyph.keys(),
    ...glyphScores.pinyin.keys(),
    ...textMatches.exact.gloss.keys(),
    ...textMatches.exact.definition.keys(),
    ...textMatches.partial.gloss.keys(),
    ...textMatches.partial.definition.keys(),
  ]);
  // Drop out-of-corpus characters before resolving cards, keeping direct lookups (the query
  // itself, or a character of the typed sentence).
  if (corpusFilter !== undefined && !corpusFilter.includesAllGraphemes) {
    for (const glyph of matchedGlyphs) {
      if (
        glyph !== trimmedQuery &&
        !sentencePositionByGlyph.has(glyph) &&
        !corpusFilter.isGraphemeInCorpus(glyph)
      ) {
        matchedGlyphs.delete(glyph);
      }
    }
  }
  const cardIdsByGlyph = new Map<string, number[]>();
  if (matchedGlyphs.size > 0) {
    const rows = await contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
      columns: { grapheme: true, id: true },
      where: { grapheme: { in: [...matchedGlyphs] } },
    });
    for (const row of rows) {
      const list = cardIdsByGlyph.get(row.grapheme) ?? [];
      list.push(row.id);
      cardIdsByGlyph.set(row.grapheme, list);
    }
  }

  // Word hits stand on their own: a query can match a dictionary word while
  // matching no grapheme meaning card at all (searching "railway" finds 火车 but
  // no single character), so returning early here would silently drop them.
  if (cardIdsByGlyph.size === 0) {
    return isSentenceQuery ? [] : wordResults;
  }

  // 3) Best-pinyin bonus and SUBTLEX rank per matched glyph.
  const cardGlyphs = [...cardIdsByGlyph.keys()];
  const [pinyinMatchByGlyph, subtlexRankByGlyph] = await Promise.all([
    gradePinyinMatchByGlyph(contentDb, glyphScores.pinyinReadings, cardGlyphs, normalizedQuery),
    fetchSubtlexRankByGlyph(staticDb, cardGlyphs),
  ]);

  // 4) Aggregate. Every candidate card came from a matched glyph, so its
  // per-glyph source scores and bonuses are known directly.
  type CardScore = {
    cardId: number;
    glyph: string;
    matchedText: SearchMatchedText | null;
    matchKind: SearchMatchKind;
    score: number;
  };
  const cardScores: CardScore[] = [];
  for (const [glyph, cardIds] of cardIdsByGlyph) {
    const pinyinMatch = pinyinMatchByGlyph.get(glyph);
    // When the query is one of the glyph's own readings, a gloss or definition
    // naming it ("surname Zhi", 洙 "Zhu River", 仉 "Zhang") is that reading
    // romanized, not a meaning. Counting it scored the pinyin match twice and
    // lifted rare characters (挚, HSK 7) above 知 and 只 for "zhi".
    const isRomanizedReading = pinyinMatch?.isOwnReading === true;
    // A glyph is typed in the query or is an exact match on a reading or text. It is scored on
    // its exact text matches alone, so partial matches never reorder the exact results.
    const matchKind: SearchMatchKind =
      pinyinMatch?.isExact === true ||
      glyphScores.glyph.has(glyph) ||
      textMatches.exact.gloss.has(glyph) ||
      textMatches.exact.definition.has(glyph)
        ? "exact"
        : "partial";
    if (exactOnly && matchKind === "partial") {
      continue;
    }
    const text = textMatches[matchKind];
    const sourceSum =
      (glyphScores.glyph.get(glyph) ?? 0) +
      (glyphScores.pinyin.get(glyph) ?? 0) +
      (isRomanizedReading ? 0 : (text.gloss.get(glyph) ?? 0) + (text.definition.get(glyph) ?? 0));
    if (sourceSum === 0) {
      continue;
    }
    const glyphBonus = computeGlyphBonus(glyph, trimmedQuery);
    const score = sourceSum + glyphBonus + (pinyinMatch?.bonus ?? 0);
    const matched = isRomanizedReading ? undefined : text.matchedText.get(glyph);
    const matchedText: SearchMatchedText | null =
      matched === undefined
        ? null
        : { pinyin: matched.pinyin, source: matched.source, text: matched.text };
    for (const cardId of cardIds) {
      cardScores.push({ cardId, glyph, matchedText, matchKind, score });
    }
  }

  if (isSentenceQuery) {
    // Positional order, and only the sentence's own characters: a glyph that
    // merely shares a pinyin or definition substring with the whole sentence is
    // noise here, and it has no position to sort by. Each one is typed in the
    // query, so every result is exact.
    const sentenceCardScores: { position: number; score: CardScore }[] = [];
    for (const score of cardScores) {
      const position = sentencePositionByGlyph.get(score.glyph);
      if (position !== undefined) {
        sentenceCardScores.push({ position, score });
      }
    }
    sentenceCardScores.sort((a, b) => {
      if (a.position !== b.position) {
        return a.position - b.position;
      }
      // Same glyph, multiple meaning cards — keep the higher-scoring one first.
      return b.score.score - a.score.score;
    });
    // Words are deliberately omitted here: sentence mode walks the typed
    // sentence's own characters in order, and a word hit has no position in that
    // walk. The Words tab is hidden for a sentence query for the same reason.
    return sentenceCardScores.map(({ score }) => ({
      category: "character" as const,
      front: score.glyph,
      id: score.cardId,
      matchedText: score.matchedText,
      matchKind: score.matchKind,
    }));
  }

  cardScores.sort((a, b) => {
    if (a.score !== b.score) {
      return b.score - a.score;
    }
    // Equal relevance: the more common character first.
    const rankDelta = compareSubtlexRank(
      subtlexRankByGlyph.get(a.glyph),
      subtlexRankByGlyph.get(b.glyph),
    );
    if (rankDelta !== 0) {
      return rankDelta;
    }
    return a.glyph.localeCompare(b.glyph);
  });

  const toCharacterResult = (s: CardScore): SearchMorphemesResult => ({
    category: "character",
    front: s.glyph,
    id: s.cardId,
    matchedText: s.matchedText,
    matchKind: s.matchKind,
  });
  const byMatchKind =
    (matchKind: SearchMatchKind) =>
    (result: { matchKind: SearchMatchKind }): boolean =>
      result.matchKind === matchKind;

  // Exact matches lead, then partial ones, and characters lead within each. Both
  // streams are already internally ranked, but their scores are not comparable —
  // the word pass has no card-gloss source, so its top score is structurally lower
  // than a character's — and a learner searching a single character wants that
  // character before the compounds built from it. The Words tab shows the word
  // stream on its own, in its own order.
  return [
    ...cardScores.filter(byMatchKind("exact")).map((s) => toCharacterResult(s)),
    ...wordResults.filter(byMatchKind("exact")),
    ...cardScores.filter(byMatchKind("partial")).map((s) => toCharacterResult(s)),
    ...wordResults.filter(byMatchKind("partial")),
  ];
}

type ContentDatabase = (
  location: Parameters<NativeContentDatabase>[0],
) => Pick<ReturnType<NativeContentDatabase>, "query" | "select">;
type StaticDatabase = (
  location: Parameters<NativeStaticDatabase>[0],
) => Pick<ReturnType<NativeStaticDatabase>, "query" | "select" | "all">;
